import { useRef, useState } from 'react'

import { api } from '../api'
import { currentLang, t } from '../i18n'
import LangSwitch from '../components/LangSwitch'
import Legal from '../components/Legal'
import { appleSignIn, isIOSApp, nativePlatform, setToken } from '../platform'
import { notify } from '../telegram'
import { telegramLogin } from '../telegramLogin'

interface Props {
  onDone: () => void
}

/** Экран входа — только вне Telegram (нативное приложение, браузер).
 *  Telegram первым: у большинства игроков профиль уже есть там, и после
 *  входа они видят свой уровень и историю. Apple — для тех, у кого Telegram
 *  нет, и потому что этого требует App Store. */
export default function Login({ onDone }: Props) {
  // Язык до входа: по умолчанию испанский, переключатель сверху
  const [, rerender] = useState<string>('es')
  const [waiting, setWaiting] = useState(false)
  const [busy, setBusy] = useState(false)
  const flow = useRef<ReturnType<typeof telegramLogin> | null>(null)

  async function viaTelegram() {
    setWaiting(true)
    try {
      flow.current = telegramLogin()
      const { status, token } = await flow.current.result
      if (status === 'ok' && token) {
        setToken(token)
        onDone()
        return
      }
      if (status === 'expired') notify(t().loginExpired)
    } catch (e) {
      notify(e instanceof Error ? e.message : t().loginFailed)
    } finally {
      flow.current = null
      setWaiting(false)
    }
  }

  async function viaApple() {
    setBusy(true)
    try {
      const { identityToken, firstName } = await appleSignIn()
      const out = await api.authApple(identityToken, firstName, currentLang(), nativePlatform)
      if (!out.token) throw new Error(t().loginFailed)
      setToken(out.token)
      onDone()
    } catch (e) {
      // Человек сам закрыл окно Apple — это не ошибка, молчим
      const text = e instanceof Error ? e.message : ''
      if (!/cancel|1001/i.test(text)) notify(text || t().loginFailed)
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="login">
      <LangSwitch onChange={rerender} />
      <div className="login-brand">
        Por<span>4</span>
      </div>
      <h1 className="big" style={{ margin: '18px 0 6px' }}>
        {t().loginTitle}
      </h1>
      <p className="muted" style={{ margin: 0 }}>
        {t().loginSub}
      </p>

      {waiting ? (
        <div className="card" style={{ marginTop: 28 }}>
          <div>{t().loginWaiting}</div>
          <div className="row" style={{ gap: 8, marginTop: 12, alignItems: 'stretch' }}>
            <button className="ghost" style={{ flex: 1 }} onClick={() => flow.current?.reopen()}>
              {t().loginOpenAgain}
            </button>
            <button className="ghost" style={{ flex: 1 }} onClick={() => flow.current?.cancel()}>
              {t().loginCancel}
            </button>
          </div>
        </div>
      ) : (
        <div style={{ display: 'grid', gap: 10, marginTop: 28 }}>
          <button className="login-telegram" disabled={busy} onClick={viaTelegram}>
            {t().loginTelegram}
          </button>
          <div className="muted small" style={{ textAlign: 'center' }}>
            {t().loginTelegramHint}
          </div>
          {isIOSApp && (
            <button className="login-apple" disabled={busy} onClick={viaApple}>
               {t().loginApple}
            </button>
          )}
        </div>
      )}
      <Legal agree />
    </div>
  )
}
